import React, { HTMLAttributes, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getReceiptParkingList } from "../../../apis/parking";
import type { IGetReceiptParkingListResponse } from "../../../apis/parking/type";
import { ReactComponent as ArrowRightBlackIcon } from "../../../assets/icons/parkSelectArrowRight.svg";
import BaseTabs from "../../../components/atom/baseTabs";
import BaseTitle from "../../../components/atom/baseTitle";
import { getLocationTag } from "../../../helper/format";
import useGeneralStore from "../../../store/general";
import useReceiptStore from "../../../store/receipt";

interface ISelectReceiptParkingPage {
  goNext: () => void;
  goStep: (step: number) => void;
}

type TSelectReceiptParkingPageProps = ISelectReceiptParkingPage &
  HTMLAttributes<HTMLDivElement>;

const SelectReceiptParkingPage: React.FC<TSelectReceiptParkingPageProps> = (
  props
) => {
  const { goNext, goStep, ...otherProps } = props;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search); // Parse the query string
  const parking_id = queryParams.get("id");

  const [filteredParkingList, setFilteredParkingList] = React.useState<
    IGetReceiptParkingListResponse[]
  >([]);

  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);
  const {
    receiptParkingList,
    setReceiptParkingList,
    selectedId,
    setSelectedId,
  } = useReceiptStore((state) => state);

  const tabs = [
    {
      label: "九龍",
      subLabel: "Kowloon",
      key: "kl",
      content: (
        <ReceiptParkingListContentTab
          contentList={filteredParkingList}
          onCarparkSelected={goNext}
        />
      ),
    },
    {
      label: "香港",
      subLabel: "Hong Kong",
      key: "hk",
      content: (
        <ReceiptParkingListContentTab
          contentList={filteredParkingList}
          onCarparkSelected={goNext}
        />
      ),
    },
    {
      label: "新界",
      subLabel: "New Territories",
      key: "nt",
      content: (
        <ReceiptParkingListContentTab
          contentList={filteredParkingList}
          onCarparkSelected={goNext}
        />
      ),
    },
  ];

  // get parking list api
  const fetchAllParkingList = async () => {
    try {
      setGlobalLoadingState(true);
      const carparkResponse = await getReceiptParkingList();
      if (carparkResponse) {
        setReceiptParkingList(carparkResponse);
        setGlobalLoadingState(false);
      }
    } catch (error) {
      setGlobalLoadingState(false);
    } finally {
      setGlobalLoadingState(false);
    }
  };

  useEffect(() => {
    fetchAllParkingList();
  }, []);

  useEffect(() => {
    updateDistrictList(0);
  }, [receiptParkingList]);

  // when district tab clicked
  const updateDistrictList = (index: number) => {
    const chosenKey = tabs[index].key;

    const filteredList = receiptParkingList.filter((parking) => {
      const matchObject = getLocationTag(parking.parking.district_id);
      return matchObject.key === chosenKey;
    });
    setFilteredParkingList(filteredList);
  };

  // when parking_id is provided in query params
  useEffect(() => {
    if (parking_id) {
      const existingCarpark = receiptParkingList.find(
        (parking) => parking.parking_id.toString() === parking_id
      );
      if (existingCarpark) {
        setSelectedId(existingCarpark.parking_id);
        goNext();
      }
    }
  }, [parking_id, receiptParkingList]);

  return (
    <div className="py-4" {...otherProps}>
      <BaseTitle
        text="下載電子收據"
        subTitle="Download E-Receipt"
        textColor="text-white"
      />

      <div className="flex flex-col gap-3 text-white font-medium text-sm text-center my-4">
        選擇停車場 Select Parking
      </div>

      <BaseTabs
        tabs={tabs}
        onTabClick={updateDistrictList}
        className="rounded bg-transparent"
        tabBarContainerStyle="flex-1 flex-nowrap gap-1 rounded text-sm leading-4 h-fit min-w-fit py-1"
        activeTextColor="text-black"
        activeBgColor="bg-mainYellow"
      />
    </div>
  );
};

const ReceiptParkingListContentTab = ({
  contentList,
  onCarparkSelected,
}: {
  contentList: IGetReceiptParkingListResponse[];
  onCarparkSelected: (index: number) => void;
}) => {
  const navigate = useNavigate(); // Initialize the navigate function
  const location = useLocation();

  const selectCarparkHandler = (carparkID: number) => {
    // store selected carpark id into store
    useReceiptStore.getState().setSelectedId(carparkID);
    // Append parking_id as a query parameter without reloading the page
    const queryParams = new URLSearchParams(location.search);
    queryParams.set("id", carparkID.toString());
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true,
    });
    onCarparkSelected(2);
  };

  return (
    <div className="grid grid-cols-1 gap-2">
      {contentList.length === 0 && (
        <div className="col-span-2 text-center text-white font-bold text-xl">
          此地區暫沒有停車場
        </div>
      )}

      {contentList.map((parking) => {
        return (
          <div
            className="relative flex flex-row group border border-white rounded-lg cursor-pointer p-3 gap-4 hover:bg-mainYellow hover:bg-opacity-30"
            key={parking.parking_id}
            onClick={() => selectCarparkHandler(parking.parking_id)}
          >
            {/* cover */}
            <img
              className="rounded-lg aspect-video w-[126px] h-[88px]"
              src={parking.parking.cover}
              alt={parking.parking.display_name}
            />

            <div className="flex flex-row flex-1 gap-2 justify-between mt-2">
              <div className="flex flex-col flex-1 gap-1 text-xs">
                <div className="min-w-8 text-center font-medium text-xs text-black rounded px-2 py-1 bg-mainYellow w-fit">
                  {parking.parking.name}
                </div>
                <div className="text-white">{parking.parking.display_name}</div>
                {/* <div className="text-white">{parking.parking.location}</div> */}
                {/* <div className="text-white">{parking.parking.location_en}</div> */}
              </div>
              <div className="flex justify-center items-center rounded-full mt-auto w-9 h-9 border border-white">
                <ArrowRightBlackIcon width={14} height={12} fill="#FFFFFF" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SelectReceiptParkingPage;
